import { LIVE_HELPER_ELEMENT_ID } from '@oneToOne/shared/components/chat/constants'
import {
  ConsentFeatures,
  type Feature,
} from '@oneToOne/shared/consent/ConsentFeatures'

import type { IFwnLiveHelper } from '@assets/fwnInit/fwnLiveHelper/types'
import { EventType } from '@core/customEvent'
import { logMessage } from '@core/logging'
import {
  getGlobalStore,
  persistedDataSelector,
  sharedStateActionsSelector,
} from '@core/store'

export type LiveHelperActionProps = Record<string, string | boolean> | undefined

/**
 * Helper to dispatch event which start call from outside of widget
 */
export const startCall = (
  liveHelperNode: Node,
  props: LiveHelperActionProps,
) => {
  liveHelperNode.dispatchEvent(
    new CustomEvent(EventType.FW_LIVE_HELPER_EXTERNAL_START_CALL, {
      detail: {
        ...props,
      },
    }),
  )
}

/**
 * Helper to dispatch event which start chat from outside of widget
 */
export const startChat = (
  liveHelperNode: Node,
  props: LiveHelperActionProps,
) => {
  liveHelperNode.dispatchEvent(
    new CustomEvent(EventType.FW_LIVE_HELPER_EXTERNAL_START_CHAT, {
      detail: {
        ...props,
      },
    }),
  )
}

/**
 * Helper to dispatch event which show widget from outside of widget
 */
export const showWidget = (
  liveHelperNode: Node,
  props: LiveHelperActionProps,
) => {
  liveHelperNode.dispatchEvent(
    new CustomEvent(EventType.FW_LIVE_HELPER_EXTERNAL_SHOW_WIDGET, {
      detail: {
        ...props,
      },
    }),
  )
}

/** Lazy loads fw-live-helper into DOM */
export const addLiveHelper = (): Node => {
  let liveHelperEl = document.querySelector(LIVE_HELPER_ELEMENT_ID)
  if (liveHelperEl) {
    return liveHelperEl
  }
  liveHelperEl = document.createElement(LIVE_HELPER_ELEMENT_ID)
  liveHelperEl.id = 'fw-lazy-load-live-helper'
  document.body.appendChild(liveHelperEl)
  return liveHelperEl
}

// TODO isLiveHelperREady should not be part of shared module, investigate better way
// how to handle ready state of live helper
// https://fwn.atlassian.net/browse/OTO-1098
let isLiveHelperReady = false
document.addEventListener(
  EventType.FW_LIVE_HELPER_READY,
  () => {
    isLiveHelperReady = true
  },
  {
    once: true,
  },
)
const executeWhenLiveHelperReady = (
  liveHelperNode: Node,
  props: LiveHelperActionProps,
  callback: (liveHelperNode: Node, props: LiveHelperActionProps) => void,
) => {
  if (isLiveHelperReady) {
    callback(liveHelperNode, props)
  } else {
    document.addEventListener(
      EventType.FW_LIVE_HELPER_READY,
      () => {
        isLiveHelperReady = true
        callback(liveHelperNode, props)
      },
      {
        once: true,
      },
    )
  }
}

export const liveHelperActions: IFwnLiveHelper['liveHelper']['actions'] = {
  startCall: (props) => {
    logMessage('FW Live Helper: startCall action called', {
      data: {
        props,
      },
    })
    const liveHelperNode = addLiveHelper()
    executeWhenLiveHelperReady(liveHelperNode, props, startCall)
  },
  startChat: (props) => {
    logMessage('FW Live Helper: startChat action called', {
      data: {
        props,
      },
    })
    const liveHelperNode = addLiveHelper()
    executeWhenLiveHelperReady(liveHelperNode, props, startChat)
  },
  showWidget: (props) => {
    logMessage('FW Live Helper: showWidget action called', {
      data: {
        props,
      },
    })
    const liveHelperNode = addLiveHelper()
    executeWhenLiveHelperReady(liveHelperNode, props, showWidget)
  },
  enableFeatures: (features: Feature[]) => {
    const consentFeatures = persistedDataSelector(
      getGlobalStore().getState(),
      'features',
    )
    if (consentFeatures) {
      try {
        const newConsentFeatures = ConsentFeatures.fromJSON(consentFeatures)

        if (newConsentFeatures) {
          newConsentFeatures.enableFeatures(features)

          sharedStateActionsSelector(
            getGlobalStore().getState(),
          ).setPersistedData('features', newConsentFeatures.toJSON())
        }
      } catch (e) {
        sharedStateActionsSelector(
          getGlobalStore().getState(),
        ).setPersistedData('features', '{}')
      }
    }
  },
  disableFeatures: (features: Feature[]) => {
    logMessage('FW Live Helper: disableFeatures action called', {
      data: {
        features,
      },
    })
    const consentFeatures = persistedDataSelector(
      getGlobalStore().getState(),
      'features',
    )
    if (consentFeatures) {
      try {
        const newConsentFeatures = ConsentFeatures.fromJSON(consentFeatures)

        if (newConsentFeatures) {
          newConsentFeatures.disableFeatures(features)

          sharedStateActionsSelector(
            getGlobalStore().getState(),
          ).setPersistedData('features', newConsentFeatures.toJSON())
        }
      } catch (e) {
        sharedStateActionsSelector(
          getGlobalStore().getState(),
        ).setPersistedData(
          'features',
          JSON.stringify(
            features.reduce((acc, f) => ({ ...acc, [f]: false }), {}),
          ),
        )
      }
    } else {
      sharedStateActionsSelector(getGlobalStore().getState()).setPersistedData(
        'features',
        JSON.stringify(
          features.reduce((acc, f) => ({ ...acc, [f]: false }), {}),
        ),
      )
    }
  },
}

export const TEST = {
  resetReadyState: () => {
    isLiveHelperReady = false
  },
}
